import { getOrderFieldKey } from '$lib/services/contents/collection/entries/reorder/config';
import { getEntryDirPath, getSharedEntryFileName } from '$lib/services/contents/collection/nested';
import { STATIC_DRAFT_KEYS } from '$lib/services/contents/draft';
import { getSlugEditorProp } from '$lib/services/contents/draft/create';
import { copyEntryRelativeAssets } from '$lib/services/contents/draft/create/duplicate-assets';
import { createProxy, isDuplicatedField } from '$lib/services/contents/draft/create/proxy.svelte';
import { fillUuidValues } from '$lib/services/contents/draft/create/uuid';
import { showDuplicateToast } from '$lib/services/contents/editor';
import { getAliasesKey, removeAliases } from '$lib/services/contents/entry/aliases';
import { getDraftFieldArgs, getField } from '$lib/services/contents/entry/fields';
import { hasUuidTag } from '$lib/services/contents/fields/compute/helpers';
import { getDefaultValueMap as getHiddenFieldDefaultValueMap } from '$lib/services/contents/fields/hidden/defaults';
import { getInitialValue as getInitialUuidValue } from '$lib/services/contents/fields/uuid/helpers';
import { getDraftI18nConfig } from '$lib/services/contents/i18n/config';
import { isFieldTranslatable } from '$lib/services/contents/i18n/fields';
import { createState, getSnapshot } from '$lib/services/utils/state.svelte';

/**
 * @import { EntryDraftState } from '$lib/services/contents/draft/state.svelte';
 * @import {
 * EntryDraft,
 * FlattenedEntryContent,
 * GetFieldArgs,
 * InternalLocaleCode,
 * LocaleContentMap,
 * } from '$lib/types/private';
 * @import { ComputeField, FieldKeyPath, HiddenField, UuidField } from '$lib/types/public';
 */

/**
 * Find the Hidden field the given key path belongs to. A Hidden field takes a value of any shape,
 * and an object or array value is flattened into key paths below the field’s own, like `tags.0`,
 * which no field configuration describes, so look for the field along the key path from the top.
 * @param {GetFieldArgs} args Arguments.
 * @returns {{ fieldConfig: HiddenField, keyPath: FieldKeyPath } | undefined} The field and its key
 * path, or `undefined` if the key path doesn’t belong to a Hidden field.
 */
const findHiddenField = ({ keyPath, ...getFieldArgs }) => {
  const segments = keyPath.split('.');

  for (let length = 1; length <= segments.length; length += 1) {
    const parentKeyPath = segments.slice(0, length).join('.');
    const fieldConfig = getField({ ...getFieldArgs, keyPath: parentKeyPath });

    if (fieldConfig?.widget === 'hidden') {
      return { fieldConfig: /** @type {HiddenField} */ (fieldConfig), keyPath: parentKeyPath };
    }
  }

  return undefined;
};

/**
 * Reset the values of a locale that must be unique to each entry: remove the canonical slug, the
 * URL aliases and the manual sort order, and give the UUID, Compute and Hidden fields new values.
 * The value map is modified in place.
 * @param {object} args Arguments.
 * @param {Omit<GetFieldArgs, 'valueMap' | 'keyPath'>} args.getFieldArgs Arguments for
 * `getField()`, without the value map and the key path.
 * @param {InternalLocaleCode} args.locale Locale code.
 * @param {InternalLocaleCode} args.defaultLocale Default locale code.
 * @param {FlattenedEntryContent} args.valueMap Detached copy of the locale’s values.
 * @param {string} args.canonicalSlugKey Property name of the canonical slug.
 * @param {string | undefined} args.aliasesKey Property name of the entry’s URL aliases.
 * @param {string | undefined} args.orderFieldKey Property name of the entry order field.
 * @returns {{ duplicatedHiddenKeyPaths: Set<FieldKeyPath>, clearedUuidFields: Map<FieldKeyPath,
 * UuidField> }} Key paths of the duplicated Hidden fields, which take the default locale’s new
 * values once those are in place, and the duplicated UUID fields cleared for the same reason, along
 * with their configuration. Both are only populated in a non-default locale.
 */
export const resetUniqueValues = ({
  getFieldArgs: baseGetFieldArgs,
  locale,
  defaultLocale,
  valueMap,
  canonicalSlugKey,
  aliasesKey,
  orderFieldKey,
}) => {
  /** @type {Set<FieldKeyPath>} */
  const duplicatedHiddenKeyPaths = new Set();
  /** @type {Map<FieldKeyPath, UuidField>} */
  const clearedUuidFields = new Map();

  // Remove the canonical slug
  delete valueMap[canonicalSlugKey];

  // Remove any redirects from the original entry’s previous paths, which must not be claimed by
  // more than one entry
  removeAliases(valueMap, aliasesKey);

  // Drop the manual sort order; a fresh value will be assigned at save time so the duplicate gets a
  // unique order even after backup/restore round trips
  if (orderFieldKey) {
    delete valueMap[orderFieldKey];
  }

  const getFieldArgs = { ...baseGetFieldArgs, valueMap };
  /** @type {Map<FieldKeyPath, HiddenField>} */
  const hiddenFields = new Map();

  // Reset some unique values
  Object.keys(valueMap).forEach((keyPath) => {
    const fieldConfig = getField({ ...getFieldArgs, keyPath });

    if (fieldConfig?.widget === 'uuid') {
      if (locale === defaultLocale || isFieldTranslatable(fieldConfig.i18n)) {
        valueMap[keyPath] = getInitialUuidValue(/** @type {UuidField} */ (fieldConfig));
      } else if (isDuplicatedField({ fieldConfig, getFieldArgs: { ...getFieldArgs, keyPath } })) {
        // Cleared for `fillUuidValues()` to copy the default locale’s new value
        valueMap[keyPath] = '';
        clearedUuidFields.set(keyPath, /** @type {UuidField} */ (fieldConfig));
      }
    }

    // A Compute field keeps the UUIDs found in its current value when it’s resolved again, so the
    // value has to be cleared for the duplicate to get UUIDs of its own. The field is resolved as
    // soon as the new draft is in place. A duplicated field is left alone in the other locales, as
    // the default locale’s new value is copied to them, so every locale shares the same UUIDs
    if (
      fieldConfig?.widget === 'compute' &&
      hasUuidTag(/** @type {ComputeField} */ (fieldConfig).value) &&
      (locale === defaultLocale || isFieldTranslatable(fieldConfig.i18n))
    ) {
      valueMap[keyPath] = '';
    }

    const hidden = findHiddenField({ ...getFieldArgs, keyPath });

    if (hidden) {
      if (locale === defaultLocale || isFieldTranslatable(hidden.fieldConfig.i18n)) {
        // An object or array value is flattened into key paths below the field’s own, e.g.
        // `tags.0`, which are replaced along with it
        delete valueMap[keyPath];
        hiddenFields.set(hidden.keyPath, hidden.fieldConfig);
      } else if (
        isDuplicatedField({
          fieldConfig: hidden.fieldConfig,
          getFieldArgs: { ...getFieldArgs, keyPath: hidden.keyPath },
        })
      ) {
        delete valueMap[keyPath];
        duplicatedHiddenKeyPaths.add(hidden.keyPath);
      }
    }
  });

  // Fill in the defaults once all the old values are gone, so a key path that comes after its own
  // items, like `tags` after `tags.0`, doesn’t take the default away again
  hiddenFields.forEach((fieldConfig, keyPath) => {
    Object.assign(
      valueMap,
      getHiddenFieldDefaultValueMap({ fieldConfig, keyPath, locale, defaultLocale }),
    );
  });

  return { duplicatedHiddenKeyPaths, clearedUuidFields };
};

/**
 * Give the duplicated fields in the non-default locales the default locale’s new values, once
 * {@link resetUniqueValues} has run for every locale. The content map is modified in place.
 * @param {object} args Arguments.
 * @param {LocaleContentMap} args.currentValues Detached copies of the values of every locale.
 * @param {InternalLocaleCode} args.defaultLocale Default locale code.
 * @param {Omit<GetFieldArgs, 'valueMap' | 'keyPath'>} args.getFieldArgs Arguments for
 * `getField()`, without the value map and the key path.
 * @param {Map<InternalLocaleCode, Set<FieldKeyPath>>} args.duplicatedHiddenFieldMap Key paths of
 * the duplicated Hidden fields in each non-default locale.
 * @param {Map<InternalLocaleCode, Map<FieldKeyPath, UuidField>>} args.clearedUuidFieldMap
 * Duplicated UUID fields cleared in each non-default locale, along with their configuration.
 */
export const propagateDuplicatedValues = ({
  currentValues,
  defaultLocale,
  getFieldArgs,
  duplicatedHiddenFieldMap,
  clearedUuidFieldMap,
}) => {
  const defaultValueMap = currentValues[defaultLocale];

  // A duplicated field holds the same value in every locale, so the other locales take the default
  // locale’s new value rather than keeping the original entry’s or getting one of their own
  duplicatedHiddenFieldMap.forEach((keyPaths, locale) => {
    keyPaths.forEach((keyPath) => {
      if (keyPath in defaultValueMap) {
        currentValues[locale][keyPath] = structuredClone(defaultValueMap[keyPath]);
      }
    });
  });

  fillUuidValues({ contentMap: currentValues, defaultLocale, getFieldArgs });

  // A list item that only exists in another locale has no default locale’s value to copy, so it
  // gets a UUID of its own rather than keeping the original entry’s or being left empty
  clearedUuidFieldMap.forEach((fieldMap, locale) => {
    fieldMap.forEach((fieldConfig, keyPath) => {
      if (!currentValues[locale][keyPath]) {
        currentValues[locale][keyPath] = getInitialUuidValue(fieldConfig);
      }
    });
  });
};

/**
 * Create the state of the duplicate draft from the original draft, along with the value proxies.
 * @param {object} args Arguments.
 * @param {EntryDraft} args.draft Original draft, which is discarded.
 * @param {LocaleContentMap} args.currentValues Reset values of the duplicate.
 * @param {EntryDraft['files']} args.files Files of the duplicate.
 * @returns {EntryDraft} New draft.
 */
export const createDuplicateDraftState = ({ draft, currentValues, files }) => {
  const { collection, collectionFile, currentPath } = draft;

  const duplicatePath =
    currentPath !== undefined && getSharedEntryFileName(collection)
      ? getEntryDirPath(currentPath)
      : currentPath;

  // The original draft is discarded, so the rest of its state can be carried over as is
  /** @type {EntryDraft} */
  const newDraft = createState(
    {
      ...draft,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      isNew: true,
      originalEntry: undefined,
      originalSlugs: {},
      currentSlugs: {},
      // The duplicate is filed alongside the original, not inside it. Where every entry owns a
      // folder, `currentPath` is the original’s own folder, so the copy has to start from its
      // parent and get a folder of its own there
      originalPath: duplicatePath,
      currentPath: duplicatePath,
      // The duplicate starts from its own values and locales, not the original’s, so that a revert
      // doesn’t bring back the original’s UUIDs, aliases or asset paths. The draft is new, so it
      // can still be saved without any change
      originalLocales: getSnapshot(draft.currentLocales),
      currentLocales: getSnapshot(draft.currentLocales),
      originalValues: structuredClone(currentValues),
      // The value proxies are created below, as they need a reference to the new draft
      currentValues: {},
      files,
      // Reset the validities
      validities: Object.fromEntries(Object.keys(draft.validities).map((locale) => [locale, {}])),
      slugEditor: getSlugEditorProp({ collection, collectionFile, originalSlugs: {} }),
      interacted: false,
    },
    STATIC_DRAFT_KEYS,
  );

  Object.entries(currentValues).forEach(([locale, valueMap]) => {
    newDraft.currentValues[locale] = createProxy({ draft: newDraft, locale, target: valueMap });
  });

  return newDraft;
};

/**
 * Duplicate the entry draft open in the editor, replacing it with the duplicate.
 * @param {EntryDraftState} entryDraft Entry draft state.
 * @returns {Promise<EntryDraft | undefined>} Duplicated draft, or `undefined` if the editor was
 * closed or given another draft while the original’s assets were being copied.
 */
export const duplicateDraft = async (entryDraft) => {
  const draft = /** @type {EntryDraft} */ (entryDraft.current);
  const { collection, fields } = draft;

  const {
    defaultLocale,
    canonicalSlug: { key: canonicalSlugKey },
  } = getDraftI18nConfig(draft);

  const getFieldArgs = getDraftFieldArgs(draft);
  const orderFieldKey = getOrderFieldKey(collection);
  const aliasesKey = getAliasesKey({ collection, fields });

  // Work on detached copies of the values: the duplicate gets value proxies of its own below, and
  // changing the original draft’s values here would needlessly run their revalidation
  /** @type {LocaleContentMap} */
  const currentValues = Object.fromEntries(
    Object.entries(draft.currentValues).map(([locale, valueMap]) => [
      locale,
      getSnapshot(valueMap),
    ]),
  );

  /** @type {Map<InternalLocaleCode, Set<FieldKeyPath>>} */
  const duplicatedHiddenFieldMap = new Map();
  /** @type {Map<InternalLocaleCode, Map<FieldKeyPath, UuidField>>} */
  const clearedUuidFieldMap = new Map();

  Object.entries(currentValues).forEach(([locale, valueMap]) => {
    const { duplicatedHiddenKeyPaths, clearedUuidFields } = resetUniqueValues({
      getFieldArgs,
      locale,
      defaultLocale,
      valueMap,
      canonicalSlugKey,
      aliasesKey,
      orderFieldKey,
    });

    duplicatedHiddenFieldMap.set(locale, duplicatedHiddenKeyPaths);
    clearedUuidFieldMap.set(locale, clearedUuidFields);
  });

  propagateDuplicatedValues({
    currentValues,
    defaultLocale,
    getFieldArgs,
    duplicatedHiddenFieldMap,
    clearedUuidFieldMap,
  });

  // The original’s own assets have to be copied along with the entry, or the duplicate would
  // reference files that only exist next to the original
  // @see https://github.com/sveltia/sveltia-cms/issues/526
  const files = { ...draft.files, ...(await copyEntryRelativeAssets({ draft, currentValues })) };

  if (entryDraft.current !== draft) {
    return undefined;
  }

  const newDraft = createDuplicateDraftState({ draft, currentValues, files });

  entryDraft.current = newDraft;
  showDuplicateToast.current = true;

  return newDraft;
};
